import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';

export interface DebugTransform {
  rotX: number; rotY: number; rotZ: number;
  posX: number; posY: number; posZ: number;
  [key: string]: number;
}

@Component({
  selector: 'app-session-object-heatmap-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, CdkDrag, CdkDragHandle],
  template: `
    <div *ngIf="objectName" cdkDrag
         class="absolute top-20 left-4 bg-black border border-zaxis-border/90 border-zaxis-accent/50 rounded-none p-4 w-[280px] shadow-pixel z-20 animate-panel-in">

      <!-- Header -->
      <div cdkDragHandle class="flex justify-between items-start mb-3 cursor-move hover:bg-white/5 -mx-2 -mt-2 p-2 rounded-none transition-colors">
        <div class="flex flex-col">
          <span class="text-[10px] font-bold text-zaxis-accent uppercase tracking-wider">Heatmap Mode</span>
          <h3 class="font-mono font-bold tracking-widest text-white uppercase text-base truncate pr-2 pointer-events-none">{{ objectName }}</h3>
        </div>
        <button (click)="clearSelection.emit()" (mousedown)="$event.stopPropagation()"
                class="text-gray-400 hover:text-white w-6 h-6 flex items-center justify-center rounded-none bg-white/5 hover:bg-white/10 pointer-events-auto">&times;</button>
      </div>

      <!-- Stats -->
      <div class="space-y-3">
        <div class="bg-black/30 rounded-none p-3 border border-zaxis-border relative overflow-hidden">
          <div class="text-[10px] text-gray-400 mb-1 relative z-10">Tiempo de atención (Hits)</div>
          <div class="flex items-end gap-2 relative z-10">
            <span class="text-xl font-mono font-bold tracking-widest text-white uppercase">
              {{ heatmapStats?.hits || 0 }}
              <span class="text-sm font-normal text-gray-400">({{ formatDuration((heatmapStats?.hits || 0) * hitDuration) }})</span>
            </span>
            <span class="text-xs text-gray-400 mb-0.5" *ngIf="objectStats">
              ({{ objectStats.hitPercentage | number:'1.1-1' }}% del total)
            </span>
          </div>
          <div class="absolute bottom-0 left-0 h-1 bg-zaxis-accent/30" [style.width.%]="objectStats?.hitPercentage || 0"></div>
        </div>
        <div class="bg-black/30 rounded-none p-3 border border-zaxis-border relative overflow-hidden">
          <div class="text-[10px] text-gray-400 mb-1 relative z-10">Interacciones (Grabs/Clicks)</div>
          <div class="flex items-end gap-2 relative z-10">
            <span class="text-xl font-mono font-bold tracking-widest text-white uppercase">{{ heatmapStats?.interactions || 0 }}</span>
            <span class="text-xs text-gray-400 mb-0.5" *ngIf="objectStats && heatmapStats?.interactions > 0">
              ({{ objectStats.interactionPercentage | number:'1.1-1' }}% del total)
            </span>
          </div>
          <div class="absolute bottom-0 left-0 h-1 bg-purple-500/30" [style.width.%]="objectStats?.interactionPercentage || 0"></div>
        </div>
      </div>

      <!-- Debug Transform -->
      <div class="mt-4 bg-black/30 rounded-none p-3 border border-zaxis-border relative z-10">
        <div class="flex justify-between items-center mb-2">
          <span class="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Alineación 3D FBX</span>
          <button (click)="editModeToggled.emit()" [disabled]="playing" [class.opacity-50]="playing"
                  class="relative inline-flex h-4 w-8 items-center rounded-none transition-colors focus:outline-none"
                  [ngClass]="editModeEnabled ? 'bg-zaxis-accent' : 'bg-gray-600'">
            <span class="inline-block h-3 w-3 transform rounded-none bg-white transition-transform"
                  [ngClass]="editModeEnabled ? 'translate-x-4' : 'translate-x-1'"></span>
          </button>
        </div>

        <div *ngIf="editModeEnabled" class="flex flex-col gap-2 animate-panel-in">
          <!-- Rotation -->
          <ng-container *ngFor="let axis of [
            {label: 'X', color: 'red', key: 'rotX', accentClass: 'accent-red-500'},
            {label: 'Y', color: 'green', key: 'rotY', accentClass: 'accent-green-500'},
            {label: 'Z', color: 'blue', key: 'rotZ', accentClass: 'accent-blue-500'}
          ]">
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono font-bold w-3" [ngClass]="'text-' + axis.color + '-400'">{{ axis.label }}</span>
              <input type="range" min="-180" max="180" step="1"
                     [ngModel]="transform[axis.key]" (ngModelChange)="onTransformChange(axis.key, $event)"
                     class="w-full h-1 bg-black rounded-none appearance-none cursor-pointer flex-grow" [ngClass]="axis.accentClass">
              <input type="number" min="-180" max="180" step="1"
                     [ngModel]="transform[axis.key]" (ngModelChange)="onTransformChange(axis.key, $event)"
                     class="w-12 text-[10px] text-right bg-black border border-zaxis-border rounded-none px-1 py-0.5 text-gray-300 font-mono focus:outline-none focus:border-zaxis-accent">
            </div>
          </ng-container>

          <div class="mt-2 mb-1 flex justify-between items-center border-t border-zaxis-border pt-3">
            <span class="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Ajuste Posición FBX</span>
          </div>

          <!-- Position -->
          <ng-container *ngFor="let axis of [
            {label: 'X', color: 'red', key: 'posX', accentClass: 'accent-red-500'},
            {label: 'Y', color: 'green', key: 'posY', accentClass: 'accent-green-500'},
            {label: 'Z', color: 'blue', key: 'posZ', accentClass: 'accent-blue-500'}
          ]">
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono font-bold w-3" [ngClass]="'text-' + axis.color + '-400'">{{ axis.label }}</span>
              <input type="range" min="-200" max="200" step="0.1"
                     [ngModel]="transform[axis.key]" (ngModelChange)="onTransformChange(axis.key, $event)"
                     class="w-full h-1 bg-black rounded-none appearance-none cursor-pointer flex-grow" [ngClass]="axis.accentClass">
              <input type="number" step="0.01"
                     [ngModel]="transform[axis.key]" (ngModelChange)="onTransformChange(axis.key, $event)"
                     class="w-12 text-[10px] text-right bg-black border border-zaxis-border rounded-none px-1 py-0.5 text-gray-300 font-mono focus:outline-none focus:border-zaxis-accent">
            </div>
          </ng-container>

          <div class="flex gap-2 mt-2">
            <button (click)="resetTransform.emit()"
                    class="flex-1 py-1 bg-white/5 hover:bg-white/10 rounded-none border border-zaxis-border text-[10px] text-gray-400 transition-colors uppercase font-bold tracking-wider">Resetear</button>
            <button (click)="saveTransform.emit()" [disabled]="saving"
                    class="flex-1 py-1 bg-zaxis-accent/20 hover:bg-zaxis-accent/40 text-zaxis-accent rounded-none border border-zaxis-accent/30 text-[10px] transition-colors uppercase font-bold tracking-wider flex justify-center items-center">
              <span *ngIf="!saving">Guardar Fijar</span>
              <span *ngIf="saving" class="w-3 h-3 border-2 border-zaxis-accent border-t-transparent rounded-none animate-spin"></span>
            </button>
          </div>
        </div>
        <div *ngIf="!editModeEnabled" class="text-[10px] text-gray-500 leading-tight">
          <span *ngIf="playing">Pausa la reproducción para editar la alineación del modelo.</span>
          <span *ngIf="!playing">Activa el modo edición para corregir la rotación original del modelo.</span>
        </div>
      </div>

      <!-- Gaze density legend -->
      <div class="mt-4 bg-black/30 rounded-none p-3 border border-zaxis-border">
        <div class="flex justify-between items-end mb-1.5">
          <span class="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Densidad de Mirada</span>
        </div>
        <div class="h-1.5 w-full rounded-none bg-gradient-to-r from-[#0000ff] via-[#00ffff] via-[#00ff00] via-[#ffff00] to-[#ff0000]"></div>
        <div class="flex justify-between items-center mt-1.5 text-[9px] text-gray-500 font-medium">
          <span>Aislada</span><span>Intensa</span>
        </div>
      </div>

      <p class="text-[10px] text-gray-500 mt-4 leading-tight">
        Estás viendo el mapa de calor individual en coordenadas locales. El resto de la escena ha sido ocultada.
      </p>
    </div>
  `,
  styles: [`
    @keyframes panelIn {
      from { transform: translateX(20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    .animate-panel-in { animation: panelIn 0.2s ease-out; }
  `]
})
export class SessionObjectHeatmapPanelComponent {
  @Input() objectName: string | null = null;
  @Input() heatmapStats: any = null;
  @Input() objectStats: { hitPercentage: number; interactionPercentage: number } | null = null;
  @Input() editModeEnabled = false;
  @Input() playing = false;
  @Input() saving = false;
  @Input() hitDuration = 0;
  @Input() transform: DebugTransform = { rotX: 0, rotY: 0, rotZ: 0, posX: 0, posY: 0, posZ: 0 };

  @Output() clearSelection = new EventEmitter<void>();
  @Output() editModeToggled = new EventEmitter<void>();
  @Output() transformChange = new EventEmitter<DebugTransform>();
  @Output() resetTransform = new EventEmitter<void>();
  @Output() saveTransform = new EventEmitter<void>();

  onTransformChange(key: string, value: number) {
    this.transformChange.emit({ ...this.transform, [key]: Number(value) });
  }

  formatDuration(seconds: number): string {
    if (!seconds || seconds < 0) return '0s';
    const s = Math.floor(seconds);
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  }
}
